import { ApiError, api } from '../../lib/api'
import { toUrlEncoded } from '../../lib/form'
import { isEnglish } from '../../lib/i18n'

/**
 * The tenant's own licence — `Features/Admin/LicenseManagement/Endpoint.cs`.
 *
 *   getLicense    -> GET  /api/license
 *                    -> { IsArabic, LicenseInformation, LicenseHistory }
 *   updateLicense -> POST /admin/licensemanagement/updatelicense
 *                    FORM-ENCODED: file, copyOldUsers  -> { Result, Message }
 *
 * THE UPDATE POSTS A FORM, not JSON. `HandleUpdateLicense` calls `ReadFormAsync()` and
 * reads two lower-case keys, so this is the first call in the migration to use
 * `src/lib/form.js`. The endpoint is not changed to suit the client (R2).
 *
 * `file` is the licence file's CONTENT as text, not an upload: the handler takes a
 * `string File`, and the licence is a signed JSON document.
 *
 * EVERY NUMBER ARRIVES AS A STRING. `LicenseInformationDto` types the seat counts, the
 * duration and the days remaining as `string`, and the screen does arithmetic on them —
 * days elapsed against duration, seats used against seats bought. Converting here is the
 * difference between a progress bar and `"365" - "20"`.
 */

const toNumber = (value) => {
  const parsed = Number(String(value ?? '').trim())
  return Number.isFinite(parsed) ? parsed : 0
}

export async function getLicense(signal) {
  const dto = (await api.get('/api/license', { signal })) ?? {}
  const info = dto.LicenseInformation ?? {}

  const history = (dto.LicenseHistory ?? []).map((row) => ({
    licenseType: row.LicenseTypeString || row.LicenseType || '',
    /* The RAW `VerificationMethod` name beside the localised one — the screen tints the
       badge per type, and it cannot key that off a string that changes with the language. */
    licenseTypeKey: row.LicenseType ?? '',
    startDate: row.StartDate ? String(row.StartDate).slice(0, 10) : '',
    duration: row.Duration ?? 0,
    usersNumber: row.UsersNumber ?? 0,
    featureSet: row.FeatureSet ?? '',
    companyName: (isEnglish ? row.CompanyEnglishName : row.CompanyArabicName) || '',
  }))

  return {
    companyName: info.CompanyName || null,
    licenseType: info.LicenseType ?? '',
    startDate: info.StartDate ?? '',
    expirationDate: info.ExpirationDate ?? '',
    duration: toNumber(info.Duration),
    daysLeft: toNumber(info.DaysLeft),
    usersNumber: toNumber(info.UsersNumber),
    consumedAccounts: toNumber(info.ConsumedAccountNumber),
    remainingAccounts: toNumber(info.RemainingAccountNumber),
    /* The active term is identified by the server. Inferring it from history order selected
       the oldest licence on installations whose navigation property arrived oldest-first. */
    featureSet: info.FeatureSet ?? '',
    /* Nowhere in the payload — see COSMETIC_UI.md. The screen shows a placeholder rather
       than inventing a value. */
    operationType: null,
    history,
  }
}

/**
 * @param {object} values
 * @param {string} values.file          the licence document's text content
 * @param {boolean} values.copyOldUsers carry the previous term's accounts into the new one
 */
export async function updateLicense({ file, copyOldUsers }) {
  try {
    const data = await api.post(
      '/admin/licensemanagement/updatelicense',
      // Lower-case keys: `form["file"]`, `form["copyOldUsers"]`.
      toUrlEncoded({ file, copyOldUsers: Boolean(copyOldUsers) }),
    )
    return { ok: true, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}
